const blocks = [];
const parents = [];
const padding = '--block-padding';
let root = null;
let nullFallback = null;
let treatNullValueAs = null;

// Styles have a transition in iframes, so we need to delay the resize function until after they have changed colour
const delay = window.self !== window.top ? 150 : 0;
const ColourBlockObserver = new MutationObserver(() => {
  setTimeout(() => {
    blocks.forEach(block => block.resize());

    // HERE FOR DEBUGGING
    // mutations.forEach((mutation) => {
    //   const { target, type } = mutation;
    //   console.log('prev: ', convertToHex(getComputedStyle(target.previousElementSibling).backgroundColor));
    //   console.log('target: ', convertToHex(getComputedStyle(target).backgroundColor));
    // });
  }, delay);
});
function convertToHex(color) {
  if (!color) return;

  // Helper function to ensure hex code format
  function rgbToHex(r, g, b) {
    const toHex = n => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Remove any extra whitespace and convert to lowercase
  color = color.trim().toLowerCase();

  // Check if it's a hex color code (with or without #)
  if (/^#([0-9a-f]{3}){1,2}$/i.test(color)) {
    // Normalize 3-digit hex to 6-digit
    if (color.length === 4) {
      color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    return color;
  }

  // Check if it's an rgb or rgba color
  const rgbaMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(color);
  if (rgbaMatch) {
    const [_, r, g, b, a] = rgbaMatch.map(Number);
    if (a === 0) {
      return treatNullValueAs; // Special value for transparency
    }
    return rgbToHex(r, g, b);
  }

  // If none of the formats matched, throw an error
  console.warn("Unsupported color format", color);
}
class ColourBlock extends HTMLElement {
  constructor() {
    super();
    // Attach a shadow DOM
    this.attachShadow({
      mode: 'open'
    });

    // Add default styles
    this.styles = document.createElement('style');

    // Add the styles to the shadow DOM
    this.shadowRoot.appendChild(this.styles);

    // Get the default styles
    this.getStyles();

    // Create a slot for the light DOM content
    const slot = document.createElement('slot');
    this.shadowRoot.appendChild(slot);

    // Add the block to the list of blocks
    blocks.push(this);

    // Resize the block
    this.resize();

    // Observe the block for changes
    ColourBlockObserver.observe(this, {
      attributes: true,
      attributeFilter: ['style']
    });
  }
  getStyles() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const settings = Object.assign({
      paddingTop: `var(${padding})`,
      paddingBottom: `var(${padding})`
    }, options);
    const styles = `
      display: block;
      padding-top: ${settings.paddingTop};
      padding-bottom: ${settings.paddingBottom};
    `;

    // Add transitions if the block is in an iframe
    const transitions = `
      transition:
        color 0.1s,
        margin-top 0.2s,
        padding-top 0.2s,
        margin-bottom 0.2s,
        padding-bottom 0.2s,
        background-color 0.1s;
    `;

    // Set the styles in the shadow DOM
    this.styles.textContent = `
      :host {
        ${styles}
        ${window.self !== window.top ? transitions : ''}
      }
    `;
  }
  resize() {
    const prevSibling = this.previousElementSibling;
    const nextSibling = this.nextElementSibling;

    // Grab the default padding
    let paddingTop = `var(${padding})`;
    let paddingBottom = `var(${padding})`;

    // Remove the collapsed classes
    this.classList.remove('collapsed--top', 'collapsed--bottom');
    this.classList.toggle('dark', getComputedStyle(this).color === 'rgb(255, 255, 255)');
    if (prevSibling && this.isSameColour(this.getSiblingColour(prevSibling))) {
      paddingTop = `calc(var(${padding}) / 2)`;
      this.classList.add('collapsed--top');
    }

    // If the next sibling is has the same colour, adjust the padding
    if (nextSibling && this.isSameColour(this.getSiblingColour(nextSibling))) {
      paddingBottom = `calc(var(${padding}) / 2)`;
      this.classList.add('collapsed--bottom');
    }
    this.getStyles({
      paddingTop,
      paddingBottom
    });
  }
  isSameColour(colour) {
    // Get the current block's property value
    let property = getComputedStyle(this).backgroundColor;

    // If we want to override the null value, do that now!
    if (treatNullValueAs !== undefined && !colour) colour = treatNullValueAs;

    // If the currentBackgroundColour is transparent, add a transparent class
    this.classList.toggle('transparent', !property);
    if (property) {
      // Convert the value to HEX
      let currentBackgroundColour = convertToHex(property);

      // Compare the colours
      return currentBackgroundColour == colour;
    }

    // If the property is not found, return true if the colour is null
    if (treatNullValueAs !== undefined) return treatNullValueAs === colour;
    if (!colour) return true;
    return false;
  }
  getSiblingColour(sibling) {
    // Check if the sibling is a ColourBlock
    if (!sibling) return null;

    // Make sure the sibling is a ColourBlock
    // If not, return null as a string so even the null fallback wont be used
    // if (!(sibling instanceof ColourBlock)) return 'null';
    if (sibling.tagName !== this.tagName) return 'null';
    let property = getComputedStyle(sibling).backgroundColor;

    // Check if the property is not found
    if (!property) return null;

    // Get the sibling's property value
    let siblingBackgroundColour = getComputedStyle(sibling).backgroundColor.trim();

    // Convert the RGB value to HEX
    if (siblingBackgroundColour.indexOf('rgb') === 0) siblingBackgroundColour = convertToHex(siblingBackgroundColour);
    return siblingBackgroundColour;
  }
}

// Define the custom element
window.customElements.define('colour-block', ColourBlock);

// Observe the body for changes
const observe = () => {
  root = getComputedStyle(document.body);
  nullFallback = root.getPropertyValue('--body-primary-colour') || root.getPropertyValue('--c-white');

  // Set the default value for null, leave as null if you don't want transparent colours to interact with coloured blocks
  treatNullValueAs = convertToHex(nullFallback);

  // Observe the body for changes
  ColourBlockObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// When the document is ready, observe the body for changes
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => observe()) : observe();